import React, { MutableRefObject, useRef, useState, useEffect } from 'react';
import { useTheme } from 'styled-components';
import { StaticImageData } from 'next/image';

import useBreakpoint from 'Common/hooks/useBreakpoint';

import Section from 'Elements/Section';
import { H2 } from 'Elements/Typography';
import FlexBox from 'Elements/FlexBox/FlexBox';

import Grid, { Col } from 'Components/Grid';
import Video, { VideoProps } from 'Components/Video';

import ProgressLine, { ProgressLineMobile } from 'Campaigns/Clearing/ProgressLine';
import CtaGroup, { CtaGroupProps } from 'Campaigns/Clearing/CtaGroup';
import IconList, { IconListProps } from 'Campaigns/Clearing/IconList';
import Stat, { StatProps } from 'Campaigns/Clearing/Stat';
import CourseSearch, { CourseSearchProps } from 'Campaigns/Clearing/CourseSearch';
import ClearingButton, { ButtonProps } from 'Campaigns/Clearing/Button';
import ClearingVideo, { ClearingVideoProps } from 'Campaigns/Clearing/Video';
import SupportBlock, { ClearingSupportBlockProps } from 'Campaigns/Clearing/SupportBlock';
import TefStat, { TefStatProps } from 'Campaigns/Clearing/Stat/Tef';

import { DEFAULT_MARGIN_TOP, FadeInUpAnimation } from '../Clearing';

import { FlowWrapper, Spacer, StyledImage } from '../Clearing.styled';

export interface Props {
    intro: {
        videoTitle: string;
        video: VideoProps;
        ctaGroup: CtaGroupProps;
    };
    whySolent: {
        iconListHeading: string;
        iconList: IconListProps;
        tef: TefStatProps;
    };
    courseSearch: CourseSearchProps;
    foundation: {
        heading: string;
        text: React.ReactNode;
        iconList: IconListProps;
        stat: StatProps;
        video?: VideoProps;
        button: ButtonProps;
    };
    students: {
        heading: string;
        duoVideo: ClearingVideoProps[];
    };
    city: {
        supportBlock: ClearingSupportBlockProps;
        stat: StatProps;
        video: VideoProps;
    };
    accommodation: {
        image: {
            src: string | StaticImageData;
            alt: string;
        };
        supportBlock: ClearingSupportBlockProps;
        stat: StatProps;
    };
}

const Grades: React.FC<Props> = props => {
    const { intro, whySolent, courseSearch, foundation, students, city, accommodation } = props;

    const { colors } = useTheme();

    const [loadedSectionRefs, setLoadedSectionRefs] = useState<HTMLDivElement[] | undefined>(
        undefined
    );

    const sectionRefs = useRef([]) as MutableRefObject<HTMLDivElement[]>;

    useEffect(() => {
        setLoadedSectionRefs(sectionRefs.current);
    }, []);

    const { breakpoints } = useTheme();
    const isDesktop = useBreakpoint(breakpoints.large.value);

    return (
        <FlowWrapper>
            {isDesktop && <ProgressLine sections={loadedSectionRefs} />}
            <div
                ref={element => element && (sectionRefs.current[0] = element)}
                style={{ position: 'relative', zIndex: 1 }}
            >
                <Section id="intro" paddingTop={1.5}>
                    <Grid>
                        <Col m={8} l={5}>
                            <FadeInUpAnimation>
                                <h2>{intro.videoTitle}</h2>
                                <Video {...intro.video} />
                            </FadeInUpAnimation>
                        </Col>
                        {!isDesktop && (
                            <Col>
                                <ProgressLineMobile />
                            </Col>
                        )}
                        <Col m={[4, 11]} l={[8, 13]}>
                            <FadeInUpAnimation>
                                {isDesktop && <br />}
                                <CtaGroup
                                    {...intro.ctaGroup}
                                    align={isDesktop ? 'right' : 'left'}
                                />
                            </FadeInUpAnimation>
                        </Col>
                        {!isDesktop && (
                            <Col>
                                <ProgressLineMobile useCircle />
                            </Col>
                        )}
                    </Grid>
                </Section>
            </div>
            <div
                ref={element => element && (sectionRefs.current[1] = element)}
                style={{ marginTop: isDesktop ? '-5rem' : DEFAULT_MARGIN_TOP }}
            >
                <Section id="why-come-to-solent" padding="none">
                    <Grid>
                        <Col m={[5, 13]} l={[8, 13]} order={isDesktop ? 2 : 1}>
                            <FadeInUpAnimation>
                                <H2 color={colors.campaigns.clearing.primary.hex}>
                                    {whySolent.iconListHeading}
                                </H2>
                                <IconList {...whySolent.iconList} />
                            </FadeInUpAnimation>
                        </Col>
                        {!isDesktop && (
                            <Col order={2}>
                                <ProgressLineMobile />
                            </Col>
                        )}
                        <Col sl={[3, 11]} l={5} align="center" order={isDesktop ? 1 : 3}>
                            <FadeInUpAnimation>
                                <TefStat {...whySolent.tef} />
                            </FadeInUpAnimation>
                        </Col>
                        {!isDesktop && (
                            <Col order={4}>
                                <ProgressLineMobile useCircle />
                            </Col>
                        )}
                    </Grid>
                </Section>
            </div>
            <div
                ref={element => element && (sectionRefs.current[2] = element)}
                style={{
                    marginTop: isDesktop ? '-4rem' : DEFAULT_MARGIN_TOP,
                    position: 'relative',
                    zIndex: 4
                }}
            >
                <Section id="course-search" padding="none">
                    <Grid>
                        <Col m={8} l={5}>
                            <FadeInUpAnimation>
                                <CourseSearch {...courseSearch} />
                            </FadeInUpAnimation>
                        </Col>
                        {!isDesktop && (
                            <Col>
                                <ProgressLineMobile useCircle />
                            </Col>
                        )}
                    </Grid>
                </Section>
            </div>
            <div
                ref={element => element && (sectionRefs.current[3] = element)}
                style={{
                    marginTop: isDesktop ? '-4rem' : DEFAULT_MARGIN_TOP,
                    position: 'relative',
                    zIndex: 3
                }}
            >
                <Section id="foundation-year" padding="none">
                    <Grid>
                        <Col m={[5, 13]} l={[8, 13]} order={isDesktop ? 2 : 1}>
                            <FadeInUpAnimation>
                                <H2 color={colors.campaigns.clearing.primary.hex}>
                                    {foundation.heading}
                                </H2>
                                {foundation.text}
                                <IconList {...foundation.iconList} />
                                {foundation.video && <Video {...foundation.video} />}
                                <ClearingButton
                                    {...foundation.button}
                                    style={{ width: '100%', marginTop: '0.5rem' }}
                                />
                            </FadeInUpAnimation>
                        </Col>
                        {!isDesktop && (
                            <Col order={2}>
                                <ProgressLineMobile />
                            </Col>
                        )}
                        <Col l={5} align="center" order={isDesktop ? 1 : 3}>
                            <FadeInUpAnimation>
                                <Stat {...foundation.stat} />
                            </FadeInUpAnimation>
                        </Col>
                        {!isDesktop && (
                            <Col order={4}>
                                <ProgressLineMobile useCircle />
                            </Col>
                        )}
                    </Grid>
                </Section>
            </div>
            <div
                ref={element => element && (sectionRefs.current[4] = element)}
                style={{
                    marginTop: isDesktop ? '-5rem' : DEFAULT_MARGIN_TOP,
                    position: 'relative',
                    zIndex: 2
                }}
            >
                <Section id="students" padding="none">
                    <Grid>
                        <Col m={8} l={5}>
                            <FadeInUpAnimation>
                                <H2 color={colors.campaigns.clearing.primary.hex}>
                                    {students.heading}
                                </H2>
                                <FlexBox gap="1.5rem">
                                    {students.duoVideo.map(video => (
                                        <ClearingVideo key={video.id} {...video} />
                                    ))}
                                </FlexBox>
                            </FadeInUpAnimation>
                        </Col>
                        {!isDesktop && (
                            <Col>
                                <ProgressLineMobile />
                            </Col>
                        )}
                    </Grid>
                </Section>
            </div>
            <div
                ref={element => element && (sectionRefs.current[5] = element)}
                style={{
                    marginTop: isDesktop ? '-3rem' : DEFAULT_MARGIN_TOP,
                    position: 'relative',
                    zIndex: 1
                }}
            >
                <Section id="city" padding="none">
                    <Grid>
                        <Col l={5} align="center">
                            <FadeInUpAnimation>
                                <Stat {...city.stat} />
                            </FadeInUpAnimation>
                        </Col>
                        {!isDesktop && (
                            <Col>
                                <ProgressLineMobile useCircle />
                            </Col>
                        )}
                        <Col m={[5, 13]} l={[8, 13]}>
                            <FadeInUpAnimation>
                                <SupportBlock {...city.supportBlock} />
                                <Video {...city.video} />
                            </FadeInUpAnimation>
                        </Col>
                        {!isDesktop && (
                            <Col>
                                <ProgressLineMobile />
                            </Col>
                        )}
                    </Grid>
                </Section>
            </div>
            <div
                ref={element => element && (sectionRefs.current[6] = element)}
                style={{ marginTop: isDesktop ? '-4rem' : DEFAULT_MARGIN_TOP }}
            >
                <Section id="accommodation" padding="none">
                    <Grid>
                        <Col l={[8, 13]} align="center" order={isDesktop ? 2 : 1}>
                            <FadeInUpAnimation>
                                <Stat {...accommodation.stat} />
                            </FadeInUpAnimation>
                        </Col>
                        {!isDesktop && (
                            <Col order={2}>
                                <ProgressLineMobile useCircle />
                            </Col>
                        )}
                        <Col m={8} l={5} order={isDesktop ? 1 : 3}>
                            <FadeInUpAnimation>
                                <StyledImage
                                    src={accommodation.image.src}
                                    alt={accommodation.image.alt}
                                    width="545"
                                    height="200"
                                    style={{ display: 'block', margin: '0 auto 1rem' }}
                                />
                                <Spacer $marginTop={{ base: 0, large: '2rem' }} />
                                <SupportBlock {...accommodation.supportBlock} />
                                <Spacer $marginTop={{ base: 0, large: '5rem' }} />
                            </FadeInUpAnimation>
                        </Col>
                    </Grid>
                </Section>
            </div>
        </FlowWrapper>
    );
};

export default Grades;
